import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Blocks, Search } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Explorer = () => {
  const [blocks, setBlocks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('blocks');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [blockchainRes, txRes] = await Promise.all([
        axios.get(`${API}/blockchain`),
        axios.get(`${API}/transactions`)
      ]);
      setBlocks(blockchainRes.data.blocks.reverse());
      setTransactions(txRes.data.transactions.reverse());
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const viewBlockDetails = (block) => {
    setSelectedBlock(block);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Block Explorer
        </h1>
        <p className="text-text-secondary">Browse blocks and transactions</p>
      </div>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('blocks')}
          data-testid="blocks-tab"
          className={`px-6 py-3 rounded-sm font-bold transition-all duration-200 ${
            activeTab === 'blocks'
              ? 'bg-primary text-primary-foreground'
              : 'bg-background-subtle text-text-secondary hover:bg-background-card'
          }`}
        >
          Blocks ({blocks.length})
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          data-testid="transactions-tab"
          className={`px-6 py-3 rounded-sm font-bold transition-all duration-200 ${
            activeTab === 'transactions'
              ? 'bg-primary text-primary-foreground'
              : 'bg-background-subtle text-text-secondary hover:bg-background-card'
          }`}
        >
          Transactions ({transactions.length})
        </button>
      </div>

      {activeTab === 'blocks' && (
        <div className="space-y-4">
          {blocks.map((block) => (
            <motion.div
              key={block.index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass p-6 rounded-sm hover:border-primary/30 transition-colors duration-200 cursor-pointer"
              onClick={() => viewBlockDetails(block)}
              data-testid={`block-item-${block.index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-sm">
                    <Blocks size={24} className="text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-unbounded text-xl font-bold text-text-primary">
                      Block #{block.index}
                    </h3>
                    <p className="text-text-muted text-sm">
                      {new Date(block.timestamp * 1000).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-text-muted text-sm">Transactions</p>
                  <p className="font-bold text-primary text-xl">{block.transactions.length}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-text-muted text-sm min-w-[120px]">Hash:</span>
                  <span className="font-mono text-sm text-text-secondary break-all">
                    {block.hash}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-text-muted text-sm min-w-[120px]">Previous Hash:</span>
                  <span className="font-mono text-sm text-text-secondary break-all">
                    {block.previous_hash}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-text-muted text-sm min-w-[120px]">Nonce:</span>
                  <span className="font-mono text-sm text-accent-success">{block.nonce}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="glass rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">Type</th>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">From</th>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">To</th>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">Amount</th>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">Token</th>
                  <th className="px-6 py-4 text-left text-text-muted text-sm font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr
                    key={idx}
                    data-testid={`tx-row-${idx}`}
                    className="border-t border-border hover:bg-background-subtle transition-colors duration-200"
                  >
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-sm font-medium">
                        {tx.tx_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-text-secondary">
                        {tx.sender.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-text-secondary">
                        {tx.recipient.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-accent-success">{tx.amount}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-text-primary font-medium">{tx.token_symbol}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-text-muted text-sm">
                        {new Date(tx.timestamp * 1000).toLocaleTimeString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedBlock && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedBlock(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass p-8 rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-unbounded text-2xl font-bold text-text-primary mb-6">
              Block #{selectedBlock.index} Details
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-text-muted text-sm">Hash</label>
                <p className="font-mono text-sm text-text-primary break-all mt-1">
                  {selectedBlock.hash}
                </p>
              </div>
              <div>
                <label className="text-text-muted text-sm">Previous Hash</label>
                <p className="font-mono text-sm text-text-primary break-all mt-1">
                  {selectedBlock.previous_hash}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-text-muted text-sm">Nonce</label>
                  <p className="font-mono text-text-primary mt-1">{selectedBlock.nonce}</p>
                </div>
                <div>
                  <label className="text-text-muted text-sm">Transactions</label>
                  <p className="font-mono text-text-primary mt-1">
                    {selectedBlock.transactions.length}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-text-muted text-sm mb-2 block">Transactions</label>
                <div className="space-y-2">
                  {selectedBlock.transactions.map((tx, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-background-subtle border border-border rounded-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-sm">
                          {tx.tx_type}
                        </span>
                        <span className="text-accent-success font-bold">
                          {tx.amount} {tx.token_symbol}
                        </span>
                      </div>
                      <p className="text-xs text-text-muted">
                        From: <span className="font-mono text-text-secondary">{tx.sender}</span>
                      </p>
                      <p className="text-xs text-text-muted">
                        To: <span className="font-mono text-text-secondary">{tx.recipient}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedBlock(null)}
              data-testid="close-block-details"
              className="mt-6 w-full py-3 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary-hover transition-colors duration-200"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Explorer;
